import React, { useEffect, useRef, useState } from 'react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Check, Gift, Heart, Mail, Sparkles, Download } from 'lucide-react';
import { toPng } from 'html-to-image';
import PageHero from '../components/PageHero';
import SpecialCard from '../components/SpecialCard';
import { SPECIAL_DAYS as DEFAULT_DAYS } from '../data/mock';
import { api } from '../lib/api';
import { EditRegion } from '../components/AdminEditBar';

const TIERS = [200, 500, 750, 1000, 1250, 2000];

export default function SpecialDay() {
  useSEO('special_day');
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [step, setStep] = useState(0);
  const [occasion, setOccasion] = useState(DEFAULT_DAYS[0]);
  const [amount, setAmount] = useState(500);
  const [custom, setCustom] = useState('');
  const [info, setInfo] = useState({ name: '', surname: '', email: '', phone: '', city: 'Istanbul' });
  const [card, setCard] = useState({ toName: '', toEmail: '', note: '' });
  const [payment, setPayment] = useState({ number: '', exp: '', cvc: '', holder: '' });
  const cardRef = useRef(null);
  const final = custom ? parseInt(custom) : amount;
  const fromName = `${info.name || ''} ${info.surname || ''}`.trim();

  useEffect(() => {
    api.publicContent('special_days').then(list => {
      const arr = (list || []).map(it => ({
        id: it.data?.id_key || it.id,
        ...(it.data || {}),
      }));
      if (arr.length > 0) {
        setDays(arr);
        setOccasion(arr[0]);
      }
    }).catch(() => {});
  }, []);

  const next = () => {
    if (step === 0 && !occasion) { toast.error('Lütfen bir özel gün seçin.'); return; }
    if (step === 1 && final < 10) { toast.error('Lütfen bir tutar seçin.'); return; }
    if (step === 2 && (!info.name || !info.email)) { toast.error('Ad ve e-posta zorunludur.'); return; }
    setStep(s => s + 1);
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: '#FAF7F2' });
      const link = document.createElement('a');
      link.download = `yekuv-${occasion?.id}-karti.png`;
      link.href = dataUrl;
      link.click();
      toast.success('Kart indirildi');
    } catch (err) {
      toast.error('Kart indirilemedi');
    }
  };

  const complete = async (e) => {
    e.preventDefault();
    if (!payment.number || !payment.cvc) { toast.error('Kart bilgilerini doldurun.'); return; }
    try {
      await api.createDonation({
        amount: final,
        recurring: false,
        info,
        note: `Özel Gün: ${occasion.title}`,
        card_type: 'special_day',
        card_data: {
          occasion_id: occasion.id,
          occasion_title: occasion.title,
          occasion_tone: occasion.tone,
          occasion_emoji: occasion.emoji,
          to_name: card.toName,
          to_email: card.toEmail,
          from_name: fromName,
          message: card.note,
        },
      });
      const target = card.toEmail ? `${card.toEmail} adresine` : 'e-postanıza';
      toast.success(`₺${final.toLocaleString('tr-TR')} bağışınız alındı! Kart ${target} gönderilecek.`);
      setStep(4);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bağış kaydedilemedi.');
    }
  };

  return (
    <EditRegion label="Özel Gün Bağışı" to="/admin/special-days">
      <>
      <PageHero slug="special_day"
        kicker="Özel Gün Bağışı"
        title={<>Her <span className="italic text-coral-200">özel gününüz</span><br />bir çocuğun hikâyesi olsun.</>}
        text="Doğum günü, evlilik yıl dönümü, anma veya özel günleriniz için sevdiklerinize anlamlı bir hediye: bir çocuğun eğitimine destek, özel tasarım bir kart ile birlikte gönderilir."
      />

      <section className="bg-cream">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-20">
          <div className="grid lg:grid-cols-12 gap-10">
            {/* Main step area */}
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-8 flex-wrap">
                {['Özel Gün', 'Tutar', 'Bilgileriniz', 'Kart & Ödeme'].map((label, i) => (
                  <React.Fragment key={i}>
                    <div className="flex items-center gap-2">
                      <div className={`w-9 h-9 rounded-full grid place-items-center text-sm font-semibold transition-colors ${step >= i ? 'bg-ink-600 text-cream' : 'bg-cream-200 text-ink-400'}`}>
                        {step > i ? <Check className="w-4 h-4" /> : i + 1}
                      </div>
                      <span className={`text-sm ${step >= i ? 'text-ink-600 font-medium' : 'text-ink-400'}`}>{label}</span>
                    </div>
                    {i < 3 && <div className={`flex-1 min-w-[20px] h-px ${step > i ? 'bg-ink-600' : 'bg-ink-100'}`} />}
                  </React.Fragment>
                ))}
              </div>

              {step === 0 && (
                <div className="bg-white rounded-3xl p-8 md:p-10 border border-ink-100 shadow-xl shadow-ink-900/5 fade-up">
                  <h2 className="font-display text-3xl text-ink-600">Hangi özel gün?</h2>
                  <p className="text-ink-400 text-sm mt-1">Seçtiğiniz özel güne özel bir kart tasarlanacak ve alıcısına gönderilecek.</p>
                  <div className="mt-7 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {days.map(d => (
                      <button key={d.id} onClick={() => setOccasion(d)} className={`text-left p-4 rounded-2xl border-2 transition-all ${occasion?.id === d.id ? 'border-ink-600 bg-ink-600 text-cream' : 'border-ink-100 bg-cream-200 text-ink-600 hover:border-ink-300'}`}>
                        <div className="text-2xl">{d.emoji}</div>
                        <div className="font-display text-base mt-2 leading-tight">{d.title}</div>
                        <div className={`text-[10px] uppercase tracking-widest mt-1 ${occasion?.id === d.id ? 'text-gold' : 'text-ink-400'}`}>{d.tone}</div>
                      </button>
                    ))}
                  </div>
                  <div className="mt-8 flex justify-end">
                    <button onClick={next} className="inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-8 h-12 font-semibold hover:bg-ink-500 transition-colors">Devam <ArrowRight className="w-4 h-4" /></button>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="bg-white rounded-3xl p-8 md:p-10 border border-ink-100 shadow-xl shadow-ink-900/5 fade-up">
                  <h2 className="font-display text-3xl text-ink-600">Bağış tutarını seçin</h2>
                  <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {TIERS.map(t => (
                      <button
                        key={t}
                        onClick={() => { setAmount(t); setCustom(String(t)); }}
                        className={`h-16 rounded-2xl border-2 font-display text-xl tabular transition-colors ${final === t ? 'border-ink-600 bg-ink-600 text-cream' : 'border-ink-100 bg-cream-200 text-ink-600 hover:border-ink-300'}`}
                      >
                        ₺{t.toLocaleString('tr-TR')}
                      </button>
                    ))}
                  </div>
                  <div className="mt-6">
                    <label className="text-xs uppercase tracking-wider text-ink-400 font-semibold">Seçilen tutar</label>
                    <div className="mt-1.5 relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-500 font-display text-xl">₺</span>
                      <input
                        type="number"
                        min="0"
                        value={custom || amount}
                        onChange={e => { setCustom(e.target.value); setAmount(parseInt(e.target.value) || 0); }}
                        placeholder="0"
                        className="w-full h-14 pl-10 pr-4 rounded-xl bg-cream-200 border border-ink-100 focus:border-ink-500 outline-none text-ink-600 font-display text-xl tabular"
                      />
                    </div>
                    <div className="mt-2 text-xs text-ink-400">İstediğiniz tutarı yazabilir veya yukarıdaki seçeneklerden seçebilirsiniz.</div>
                  </div>
                  <div className="mt-8 flex justify-between">
                    <button onClick={() => setStep(0)} className="inline-flex items-center gap-2 text-ink-500 hover:text-ink-600 font-medium"><ArrowLeft className="w-4 h-4" /> Geri</button>
                    <button onClick={next} className="inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-8 h-12 font-semibold hover:bg-ink-500">Devam <ArrowRight className="w-4 h-4" /></button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="bg-white rounded-3xl p-8 md:p-10 border border-ink-100 shadow-xl shadow-ink-900/5 fade-up">
                  <h2 className="font-display text-3xl text-ink-600">Bilgileriniz</h2>
                  <div className="mt-6 grid sm:grid-cols-2 gap-4">
                    <F label="Ad *" value={info.name} onChange={v => setInfo({ ...info, name: v })} />
                    <F label="Soyad" value={info.surname} onChange={v => setInfo({ ...info, surname: v })} />
                    <F label="E-posta *" type="email" value={info.email} onChange={v => setInfo({ ...info, email: v })} />
                    <F label="Telefon" value={info.phone} onChange={v => setInfo({ ...info, phone: v })} />
                    <F label="Şehir" value={info.city} onChange={v => setInfo({ ...info, city: v })} />
                  </div>
                  <div className="mt-8 border-t border-ink-100 pt-6">
                    <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold flex items-center gap-2"><Mail className="w-4 h-4" /> Kart Bilgileri</div>
                    <p className="text-sm text-ink-400 mt-1">Bağışınız, aşağıdaki kişiye özel tasarlanan kart ile gönderilir.</p>
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                      <F label="Alıcı İsmi *" value={card.toName} onChange={v => setCard({ ...card, toName: v })} />
                      <F label="Alıcı E-posta" type="email" value={card.toEmail} onChange={v => setCard({ ...card, toEmail: v })} />
                    </div>
                    <div className="mt-4">
                      <label className="text-xs uppercase tracking-wider text-ink-400 font-semibold">Kişisel Not (Kart üzerine yazılır)</label>
                      <textarea rows={3} maxLength={280} value={card.note} onChange={e => setCard({ ...card, note: e.target.value })} placeholder="Boş bırakırsanız, seçili özel güne uygun standart metin kullanılır..." className="mt-1.5 w-full p-4 rounded-xl bg-cream-200 border border-ink-100 outline-none text-ink-600" />
                      <div className="text-[10px] text-ink-400 mt-1 text-right">{card.note.length}/280</div>
                    </div>
                  </div>
                  <div className="mt-8 flex justify-between">
                    <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 text-ink-500 hover:text-ink-600 font-medium"><ArrowLeft className="w-4 h-4" /> Geri</button>
                    <button onClick={next} className="inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-8 h-12 font-semibold hover:bg-ink-500">Devam <ArrowRight className="w-4 h-4" /></button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <form onSubmit={complete} className="bg-white rounded-3xl p-8 md:p-10 border border-ink-100 shadow-xl shadow-ink-900/5 fade-up">
                  <h2 className="font-display text-3xl text-ink-600">Ödeme</h2>
                  <div className="mt-6 grid gap-4">
                    <F label="Kart Üzerindeki İsim" value={payment.holder} onChange={v => setPayment({ ...payment, holder: v })} />
                    <F label="Kart Numarası" value={payment.number} onChange={v => setPayment({ ...payment, number: v })} />
                    <div className="grid grid-cols-2 gap-4">
                      <F label="Son Kullanma (AA/YY)" value={payment.exp} onChange={v => setPayment({ ...payment, exp: v })} />
                      <F label="CVC" value={payment.cvc} onChange={v => setPayment({ ...payment, cvc: v })} />
                    </div>
                  </div>
                  <div className="mt-6 flex items-center gap-2 text-xs text-ink-400"><Check className="w-4 h-4 text-gold" /> Bilgileriniz SSL ile güvence altındadır (mock ödeme).</div>
                  <div className="mt-8 flex justify-between">
                    <button type="button" onClick={() => setStep(2)} className="inline-flex items-center gap-2 text-ink-500 hover:text-ink-600 font-medium"><ArrowLeft className="w-4 h-4" /> Geri</button>
                    <button type="submit" className="inline-flex items-center gap-2 bg-coral-300 text-ink-700 rounded-full px-8 h-12 font-semibold hover:bg-coral-400 hover:text-cream transition-colors"><Heart className="w-4 h-4" /> ₺{final.toLocaleString('tr-TR')} Bağış Yap</button>
                  </div>
                </form>
              )}

              {step === 4 && (
                <div className="bg-white rounded-3xl p-10 border border-ink-100 shadow-xl text-center fade-up">
                  <div className="w-20 h-20 rounded-full bg-gold/20 grid place-items-center mx-auto text-gold-500"><Check className="w-10 h-10" /></div>
                  <h2 className="font-display text-4xl text-ink-600 mt-6">Teşekkür ederiz!</h2>
                  <p className="mt-3 text-ink-500 max-w-md mx-auto">"{occasion.title}" için yaptığınız ₺{final.toLocaleString('tr-TR')} bağış alındı. {card.toEmail ? `${card.toEmail} adresine özel bağış kartı gönderilecek.` : 'Makbuzunuz e-posta ile gönderildi.'}</p>
                  <div className="mt-6 flex flex-wrap justify-center gap-3">
                    <button onClick={downloadCard} className="inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-5 h-11 font-medium hover:bg-ink-500"><Download className="w-4 h-4" /> Kartı İndir</button>
                    <button onClick={() => { setStep(0); setAmount(500); setCustom(''); setCard({ toName: '', toEmail: '', note: '' }); }} className="inline-flex items-center gap-2 bg-cream-200 text-ink-600 rounded-full px-5 h-11 font-medium">Yeni Bağış</button>
                  </div>
                </div>
              )}
            </div>

            {/* Preview panel */}
            <aside className="lg:col-span-5">
              <div className="sticky top-28">
                <div className="text-[10px] uppercase tracking-widest text-coral-300 font-semibold flex items-center gap-2 mb-3"><Gift className="w-3.5 h-3.5" /> Canlı Kart Önizlemesi</div>
                <div ref={cardRef}>
                  <SpecialCard
                    occasion={occasion}
                    toName={card.toName}
                    fromName={fromName}
                    note={card.note}
                    amount={final}
                    showAmount={false}
                  />
                </div>
                <button onClick={downloadCard} className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-white border border-ink-200 text-ink-600 rounded-full px-4 h-11 text-sm font-semibold hover:bg-ink-600 hover:text-cream hover:border-ink-600 transition-colors">
                  <Download className="w-4 h-4" /> Kart Önizlemesini İndir (PNG)
                </button>
                <div className="mt-4 bg-coral-50 border border-coral-100 text-coral-400 rounded-2xl p-4 text-xs leading-relaxed flex gap-2">
                  <Sparkles className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Bağış tamamlandıktan sonra hediye kartı, alıcının e-postasına özel bir tasarımla gönderilecektir.</span>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
    </EditRegion>
  );
}

function F({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-ink-400 font-semibold">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} className="mt-1.5 w-full h-12 px-4 rounded-xl bg-cream-200 border border-ink-100 focus:border-ink-500 focus:ring-2 focus:ring-ink-500/10 outline-none text-ink-600" />
    </div>
  );
}
